<template>
  <div class="emp-notice-detail">
    <!-- 헤더 -->
    <div class="emp-header">
      <button class="back-btn" @click="goBack">
        <v-icon size="24" color="white">mdi-arrow-left</v-icon>
      </button>
      <div class="header-title">공지사항</div>
      <div class="header-spacer"></div>
    </div>

    <!-- 컨텐츠 -->
    <div class="emp-content">
      <!-- 로딩 -->
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>

      <template v-else-if="notice">
        <!-- 공지사항 내용 -->
        <div class="notice-card">
          <div class="notice-header">
            <div class="category-row">
              <span :class="['badge', `badge-${notice.nc_category.toLowerCase()}`]">
                {{ getCategoryText(notice.nc_category) }}
              </span>
              <span v-if="notice.nc_type === 'ADMIN'" class="badge badge-admin">전체</span>
            </div>
            <h1 class="notice-title">{{ notice.nc_title }}</h1>
            <div class="notice-meta">
              {{ notice.nc_type === 'ADMIN' ? '관리자' : notice.nc_sisul_name || '-' }}
              <span class="dot">·</span>
              {{ formatDate(notice.nc_wdate) }}
              <span class="dot">·</span>
              조회 {{ notice.nc_hit }}
            </div>
          </div>
          
          <div class="notice-body ql-editor" v-html="notice.nc_content"></div>
          
          <!-- 첨부파일 -->
          <div v-if="files.length > 0" class="notice-files">
            <div class="files-title">첨부파일 ({{ files.length }})</div>
            <div v-for="file in files" :key="file.nf_idx" class="file-item" @click="downloadFile(file)">
              <v-icon size="16">mdi-file</v-icon>
              <span>{{ file.nf_original_name }}</span>
              <v-icon size="16" color="primary">mdi-download</v-icon>
            </div>
          </div>
        </div>

        <!-- 공지사항 목록 -->
        <div class="other-notices">
          <div class="section-title">공지사항 목록</div>
          <div v-for="item in otherNotices" :key="item.nc_idx" class="list-item" :class="{ active: item.nc_idx === notice.nc_idx }" @click="goToNotice(item)">
            <span :class="['mini-badge', `badge-${item.nc_category.toLowerCase()}`]">{{ getCategoryShort(item.nc_category) }}</span>
            <span class="list-title">{{ item.nc_title }}</span>
            <span class="list-author">{{ item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '-' }}</span>
            <span class="list-date">{{ formatListDate(item.nc_wdate) }}</span>
          </div>
        </div>
      </template>

      <!-- 에러 -->
      <div v-else class="error-container">
        <v-icon size="40" color="#ccc">mdi-alert-circle-outline</v-icon>
        <p>공지사항을 찾을 수 없습니다</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: string;
  nc_sisul_name: string | null;
  nc_title: string;
  nc_content: string;
  nc_hit: number;
  nc_wdate: string;
}

interface NoticeFile { nf_idx: number; nf_original_name: string; nf_type: string; }

const route = useRoute();
const router = useRouter();

const loading = ref(true);
const notice = ref<Notice | null>(null);
const files = ref<NoticeFile[]>([]);
const otherNotices = ref<Notice[]>([]);

const loadNotice = async () => {
  const ncIdx = route.params.ncIdx;
  loading.value = true;
  
  try {
    const [detailRes, listRes] = await Promise.all([
      axios.get(`/api/notice/detail/${ncIdx}`, { withCredentials: true }),
      axios.get('/api/notice/list', { params: { page: 1, limit: 10 }, withCredentials: true })
    ]);
    
    if (detailRes.data.success) {
      notice.value = detailRes.data.notice;
      files.value = detailRes.data.files || [];
    }
    
    if (listRes.data.success) {
      otherNotices.value = listRes.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

const goBack = () => router.back();
const goToNotice = (item: Notice | null) => { if (item) router.replace(`/emp/notice/${item.nc_idx}`); };
const downloadFile = (file: NoticeFile) => window.open(`/api/notice/download/${file.nf_idx}`, '_blank');

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('ko-KR') : '-';
const formatShortDate = (d: string) => d ? `${new Date(d).getMonth()+1}/${new Date(d).getDate()}` : '';
const formatListDate = (d: string) => {
  if (!d) return '-';
  const date = new Date(d);
  return `${date.getFullYear()}.${String(date.getMonth()+1).padStart(2,'0')}.${String(date.getDate()).padStart(2,'0')}`;
};
const getCategoryText = (c: string) => ({ NOTICE: '공지', UPDATE: '업데이트', EVENT: '이벤트', URGENT: '긴급' }[c] || c);
const getCategoryShort = (c: string) => ({ NOTICE: '공지', UPDATE: '업뎃', EVENT: '행사', URGENT: '긴급' }[c] || c);

watch(() => route.params.ncIdx, () => loadNotice());
onMounted(() => loadNotice());
</script>

<style scoped lang="scss">
@import '@vueup/vue-quill/dist/vue-quill.snow.css';

.emp-notice-detail { min-height: 100vh; background: #f5f5f5; }

.emp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  
  .back-btn { background: none; border: none; padding: 4px; cursor: pointer; }
  .header-title { font-size: 17px; font-weight: 600; }
  .header-spacer { width: 32px; }
}

.emp-content { padding: 12px; padding-bottom: 80px; }

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px;
  color: #999;
  p { margin-top: 10px; font-size: 13px; }
}

.notice-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.notice-header {
  padding: 14px;
  border-bottom: 1px solid #eee;
  
  .category-row { margin-bottom: 8px; display: flex; gap: 6px; }
  
  .badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    &.badge-notice { background: #e3f2fd; color: #1976d2; }
    &.badge-update { background: #e0f7fa; color: #0097a7; }
    &.badge-event { background: #e8f5e9; color: #388e3c; }
    &.badge-urgent { background: #ffebee; color: #d32f2f; }
    &.badge-admin { background: #f5f5f5; color: #616161; }
  }
  
  .notice-title { font-size: 17px; font-weight: 700; color: #222; margin: 0 0 8px 0; line-height: 1.4; }
  .notice-meta { font-size: 12px; color: #888; .dot { margin: 0 4px; } }
}

.notice-body {
  padding: 14px;
  min-height: 120px;
  line-height: 1.7;
  font-size: 14px;
  :deep(img) { max-width: 100%; height: auto; }
}

.notice-files {
  padding: 12px 14px;
  background: #fafafa;
  border-top: 1px solid #eee;
  
  .files-title { font-size: 12px; font-weight: 600; color: #666; margin-bottom: 8px; }
  
  .file-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background: white;
    border-radius: 8px;
    margin-bottom: 6px;
    font-size: 12px;
    cursor: pointer;
    span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
}

.other-notices {
  margin-top: 16px;
  
  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #333;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 2px solid #333;
  }
  
  .list-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    &:active { background: #fafafa; }
    &.active { background: #e8f5e9; border-radius: 6px; padding: 8px; margin: 0 -8px; }
    
    .mini-badge {
      font-size: 10px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 4px;
      margin-right: 8px;
      &.badge-notice { background: #e3f2fd; color: #1976d2; }
      &.badge-update { background: #e0f7fa; color: #0097a7; }
      &.badge-event { background: #e8f5e9; color: #388e3c; }
      &.badge-urgent { background: #ffebee; color: #d32f2f; }
    }
    
    .list-title { flex: 1; font-size: 13px; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .list-author { font-size: 10px; color: #666; margin-left: 8px; white-space: nowrap; }
    .list-date { font-size: 10px; color: #999; margin-left: 6px; white-space: nowrap; }
  }
}
</style>
